import { apiFetch } from "@/lib/api";
import { deriveSgsLineHarvestPassForPersist } from "@integrator-src/hub/clampqc-measurement-rules";

import { clampInspectionSizeFromSku } from "./clampQcMockData";

/**
 * Explicit fixture for the local DEV sandbox job only (`clampQcIsLocalSandboxSlug`).
 * Never substitute this when the hub API responded — missing or 422 settings stay null upstream.
 */
export const SANDBOX_CLAMP_QC_HUB_SETTINGS: ClampQcHubSettingsPayload = {
  unitsPerCartonLarge: 20,
  unitsPerCartonSmall: 30,
  inductanceTestingPercentPerWorkOrder: 100,
  inductanceMinLarge: 1.0,
  inductanceMaxLarge: 12.0,
  inductanceMinSmall: 1.05,
  inductanceMaxSmall: 2.1,
  dragGrayMinLarge: 0,
  dragPassMinLarge: 1.5,
  dragPassMaxLarge: 2.5,
  dragGrayMaxLarge: 5,
  dragGrayMinSmall: 0,
  dragPassMinSmall: 0.6,
  dragPassMaxSmall: 1.4,
  dragGrayMaxSmall: 3,
  unloadedMinLarge: 25,
  unloadedMaxLarge: 30,
  unloadedMinSmall: 25,
  unloadedMaxSmall: 30,
  grayTesterLoadedMaxLarge: 25,
  grayTesterLoadedMaxSmall: 25,
};

export interface ClampQcHubSettingsPayload {
  unitsPerCartonLarge: number;
  unitsPerCartonSmall: number;
  /** Percent of planned units that must record inductance (stratified by QC ordinal); 100 = every unit. */
  inductanceTestingPercentPerWorkOrder: number;
  /** Measured inductance lower bound for SKU prefix `90-003*` (large clamp line). */
  inductanceMinLarge: number;
  /** Measured inductance upper bound for SKU prefix `90-003*`. */
  inductanceMaxLarge: number;
  /** Measured inductance lower bound for SKU prefix `90-002*` (small clamp line). */
  inductanceMinSmall: number;
  /** Measured inductance upper bound for SKU prefix `90-002*`. */
  inductanceMaxSmall: number;
  /** Drag gray window lower bound (large line `90-003*`). */
  dragGrayMinLarge: number;
  dragPassMinLarge: number;
  dragPassMaxLarge: number;
  dragGrayMaxLarge: number;
  dragGrayMinSmall: number;
  dragPassMinSmall: number;
  dragPassMaxSmall: number;
  dragGrayMaxSmall: number;
  unloadedMinLarge: number;
  unloadedMaxLarge: number;
  unloadedMinSmall: number;
  unloadedMaxSmall: number;
  grayTesterLoadedMaxLarge: number;
  grayTesterLoadedMaxSmall: number;
}

export const CLAMPQC_PHYSICAL_FAILURE_REASONS_QUERY_KEY = ["clamp-qc", "physical-failure-reasons"] as const;

export const CLAMPQC_PHYSICAL_FAILURE_REASONS_ADMIN_QUERY_KEY = [
  "clamp-qc",
  "physical-failure-reasons",
  "admin",
] as const;

export type ClampQcPhysicalFailureReason = {
  id: number;
  reason: string;
};

export type ClampQcPhysicalFailureReasonAdmin = ClampQcPhysicalFailureReason & {
  active: boolean;
  deactivationNote: string | null;
  deactivatedAt: string | null;
  deactivatedBy: string | null;
};

export type ClampQcPhysicalFailureReasonsResponse = {
  reasons: ClampQcPhysicalFailureReason[];
};

export type ClampQcPhysicalFailureReasonsAdminResponse = {
  reasons: ClampQcPhysicalFailureReasonAdmin[];
};

export type ClampQcPhysicalFailureReasonAddResponse = {
  reason: ClampQcPhysicalFailureReason & { active: boolean };
};

export function sortClampQcPhysicalFailReasonLabels(labels: readonly string[]): string[] {
  return [...labels].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
}

export async function fetchClampQcPhysicalFailureReasons(): Promise<ClampQcPhysicalFailureReasonsResponse> {
  return apiFetch<ClampQcPhysicalFailureReasonsResponse>("/api/clamp-qc/physical-failure-reasons", {
    cache: "no-store",
  });
}

export async function fetchClampQcPhysicalFailureReasonsAdmin(): Promise<ClampQcPhysicalFailureReasonsAdminResponse> {
  return apiFetch<ClampQcPhysicalFailureReasonsAdminResponse>("/api/clamp-qc/physical-failure-reasons/admin", {
    cache: "no-store",
  });
}

export async function appendClampQcPhysicalFailReason(reason: string): Promise<ClampQcPhysicalFailureReasonAddResponse> {
  return apiFetch<ClampQcPhysicalFailureReasonAddResponse>("/api/clamp-qc/physical-failure-reasons", {
    method: "POST",
    body: { reason },
    cache: "no-store",
  });
}

export async function deactivateClampQcPhysicalFailReason(
  id: number,
  note: string,
): Promise<{ reason: ClampQcPhysicalFailureReasonAdmin }> {
  return apiFetch<{ reason: ClampQcPhysicalFailureReasonAdmin }>(
    `/api/clamp-qc/physical-failure-reasons/${String(id)}/deactivate`,
    {
      method: "PATCH",
      body: { note },
      cache: "no-store",
    },
  );
}

export type ClampQcDragBand = "fail" | "gray" | "pass" | "unknown";

export type ClampQcDragLadder = {
  grayMin: number;
  passMin: number;
  passMax: number;
  grayMax: number;
};

/** Drag = unloaded line reading minus loaded-before-tester reading (same units on the floor). */
export function clampQcDragFromUnloadedLoadedLines(unloadedRaw: string, loadedRaw: string): number | null {
  const unloadedTrim = unloadedRaw.trim();
  const loadedTrim = loadedRaw.trim();
  if (unloadedTrim === "" || loadedTrim === "") {
    return null;
  }
  const unloaded = Number.parseFloat(unloadedTrim);
  const loaded = Number.parseFloat(loadedTrim);
  if (!Number.isFinite(unloaded) || !Number.isFinite(loaded)) {
    return null;
  }
  return unloaded - loaded;
}

export function clampQcDragLadderFromSettings(
  sku: string,
  settings: ClampQcHubSettingsPayload | null,
): ClampQcDragLadder | null {
  if (settings == null) {
    return null;
  }
  const line = clampInspectionSizeFromSku(sku);
  if (line === "large") {
    return {
      grayMin: settings.dragGrayMinLarge,
      passMin: settings.dragPassMinLarge,
      passMax: settings.dragPassMaxLarge,
      grayMax: settings.dragGrayMaxLarge,
    };
  }
  if (line === "small") {
    return {
      grayMin: settings.dragGrayMinSmall,
      passMin: settings.dragPassMinSmall,
      passMax: settings.dragPassMaxSmall,
      grayMax: settings.dragGrayMaxSmall,
    };
  }
  return null;
}

export function clampQcDragBandForDrag(drag: number | null, ladder: ClampQcDragLadder | null): ClampQcDragBand {
  if (ladder == null) {
    return "unknown";
  }
  if (drag == null || !Number.isFinite(drag)) {
    return "unknown";
  }
  if (drag < ladder.grayMin || drag > ladder.grayMax) {
    return "fail";
  }
  if (drag >= ladder.passMin && drag <= ladder.passMax) {
    return "pass";
  }
  return "gray";
}

export function clampQcDragBandForSku(
  drag: number | null,
  sku: string,
  settings: ClampQcHubSettingsPayload | null,
): ClampQcDragBand {
  return clampQcDragBandForDrag(drag, clampQcDragLadderFromSettings(sku, settings));
}

/**
 * Line harvest failure from drag rules: outside gray window fails; inside pass window passes; gray band uses the
 * operator Line harvest Good/Failed after tester. Unset gray-band choice is not a fail (persist null instead).
 */
export function clampQcLineHarvestFailsFromDragDraft(
  lineHarvestFailManual: boolean | null,
  drag: number | null,
  sku: string,
  settings: ClampQcHubSettingsPayload | null,
): boolean {
  if (settings == null) {
    return lineHarvestFailManual === null ? false : lineHarvestFailManual;
  }
  return (
    deriveSgsLineHarvestPassForPersist({
      lineHarvestFailManual,
      drag,
      sku,
      settings,
    }) === false
  );
}

export {
  clampQcInductanceAcceptanceRangeForSku,
  clampQcInductanceEntryBlocksInspectionSubmit,
  clampQcInductanceReadingFailsAcceptance,
  clampQcInductanceReadingHasInvalidNumeric,
  clampQcInductanceReadingIsOutOfBand,
} from "./clampQcInductanceValidation";

export {
  deriveSgsLineHarvestPassForPersist,
  clampQcUnloadedLineAcceptanceRangeForSku,
  clampQcUnloadedLineFailsAcceptance,
} from "@integrator-src/hub/clampqc-measurement-rules";

export type ClampQcHubSettingsResponse = ClampQcHubSettingsPayload & {
  updatedAt: string | null;
};

export const CLAMPQC_HUB_SETTINGS_QUERY_KEY = ["clamp-qc", "hub-settings"] as const;

export async function fetchClampQcHubSettings(): Promise<ClampQcHubSettingsResponse> {
  return apiFetch<ClampQcHubSettingsResponse>("/api/clamp-qc/settings", { cache: "no-store" });
}

export type ClampQcHubSettingsPatchRequest = Partial<ClampQcHubSettingsPayload>;

export async function patchClampQcHubSettings(patch: ClampQcHubSettingsPatchRequest): Promise<ClampQcHubSettingsResponse> {
  return apiFetch<ClampQcHubSettingsResponse>("/api/clamp-qc/settings", {
    method: "PATCH",
    body: patch,
    cache: "no-store",
  });
}
